// useSignInContactFlow — one contact (a mobile number or an e-mail address)
// proved by a 6-digit code and then saved with that proof.
//
// WHY (owner, 1 Oct 2026): the admin and doctor profiles showed a red "Not
// Verified" pill with no way to verify the address that was already there —
// the only code flow lived in Edit mode and only woke up when the value was
// CHANGED. The patient web had already solved this ("How you sign in",
// Frontend-patient-web/src/components/profile/ProfileDetailsTab.jsx,
// useCredentialOtp); this is that hook, ported to the tenant/Super Admin
// contract:
//   POST /auth/send-otp   { email } | { phone }
//   POST /auth/verify-otp { email|phone, otpCode } -> { proof: { token, ... } }
//   then the caller's save, with `emailProof` / `phoneProof` beside the value.
// The server sets the verified flag from the proof alone; nothing here ever
// claims "verified" on its own.
//
// Kept byte-identical in Frontend-superadmin-web (src/test/mirroredFiles.test.js).

import { useCallback, useEffect, useRef, useState } from 'react';

export const RESEND_SECONDS = 60;

/**
 * The words a failed call carries, whatever shape it arrives in: the auth
 * thunks reject with a string, UpdateAdmin with { error: string | {en, fr} },
 * UpdateDoctor with { error: <axios error> }. A bare axios sentence
 * ("Request failed with status code 400") is never shown — the fallback is.
 */
export const reasonText = (err, fallback, lang = 'en') => {
    const pick = (m) => {
        if (!m) return '';
        if (typeof m === 'string') return m;
        if (typeof m === 'object') return m[lang] || m.en || m.fr || '';
        return '';
    };
    if (!err) return fallback;
    if (typeof err === 'string') return err;
    const inner = err.error !== undefined ? err.error : err;
    if (typeof inner === 'string') return inner;
    if (inner?.response) return pick(inner.response.data?.message) || fallback;
    if (inner instanceof Error) return fallback;
    return pick(inner?.en || inner?.fr ? inner : inner?.message) || fallback;
};

/**
 * @param {object}   opts
 * @param {Function} opts.send    (value) => Promise<{ delivery? }>
 * @param {Function} opts.verify  (value, code) => Promise<proofToken>
 * @param {Function} opts.save    (value, proofToken) => Promise
 * @param {Function} [opts.onDone] (value) => void, after a successful save
 * @param {Function} opts.reason  (err, fallbackKind) => string, for errors
 * @param {Function} [opts.onLostError] (message) => void, for a failure that
 *        lands after this panel unmounted (see below)
 */
export default function useSignInContactFlow({ send, verify, save, onDone, reason, onLostError }) {
    const [open, setOpen] = useState(false);
    const [mode, setMode] = useState('verify'); // 'verify' (value fixed) | 'change' (value editable)
    const [stage, setStage] = useState('value'); // 'value' | 'code'
    const [value, setValueState] = useState('');
    const [code, setCodeState] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [delivery, setDelivery] = useState('');
    const [timer, setTimer] = useState(0);
    const intervalRef = useRef(null);
    // A proof outlives a failed SAVE: the code was right, the server just
    // refused the write (an address in use, say). Retrying must not ask for a
    // second code — the first one is spent.
    const proofRef = useRef(null);
    // The profile pages swap the whole tab for a loader while the save's
    // thunk is pending, so a refused save can come back to a panel that no
    // longer exists. Its reason must still reach the person (onLostError).
    const mountedRef = useRef(true);
    useEffect(() => {
        mountedRef.current = true;
        return () => { mountedRef.current = false; };
    }, []);

    const stopTimer = useCallback(() => {
        if (intervalRef.current) clearInterval(intervalRef.current);
        intervalRef.current = null;
    }, []);
    useEffect(() => stopTimer, [stopTimer]);

    const start = useCallback((initial = '', nextMode = 'verify') => {
        stopTimer();
        proofRef.current = null;
        setMode(nextMode);
        setValueState(initial || '');
        setCodeState('');
        setError('');
        setDelivery('');
        setTimer(0);
        setStage('value');
        setOpen(true);
    }, [stopTimer]);

    const close = useCallback(() => {
        stopTimer();
        proofRef.current = null;
        setOpen(false);
        setCodeState('');
        setError('');
    }, [stopTimer]);

    const sendCode = useCallback(async ({ resend = false } = {}) => {
        if (busy || (resend && timer > 0)) return;
        setBusy(true);
        setError('');
        try {
            const data = await send(value.trim());
            proofRef.current = null;
            setDelivery(data?.delivery || '');
            setCodeState('');
            setStage('code');
            setTimer(RESEND_SECONDS);
            stopTimer();
            intervalRef.current = setInterval(() => {
                setTimer((prev) => {
                    if (prev <= 1) { stopTimer(); return 0; }
                    return prev - 1;
                });
            }, 1000);
        } catch (err) {
            setError(reason(err, 'send'));
        } finally {
            setBusy(false);
        }
    }, [busy, timer, send, value, stopTimer, reason]);

    const verifyCode = useCallback(async () => {
        if (busy || code.length !== 6) return;
        setBusy(true);
        setError('');
        const v = value.trim();
        let stepFailed = 'verify';
        try {
            if (!proofRef.current) proofRef.current = await verify(v, code);
            stepFailed = 'save';
            await save(v, proofRef.current);
            stopTimer();
            proofRef.current = null;
            setOpen(false);
            setCodeState('');
            onDone?.(v);
        } catch (err) {
            const message = reason(err, stepFailed);
            if (mountedRef.current) setError(message);
            else onLostError?.(message);
        } finally {
            setBusy(false);
        }
    }, [busy, code, value, verify, save, onDone, stopTimer, reason, onLostError]);

    return {
        open, mode, stage, value, code, busy, error, delivery, timer,
        setValue: (v) => { setValueState(v); setError(''); },
        setCode: (v) => { setCodeState(String(v).replace(/\D/g, '').slice(0, 6)); setError(''); },
        start, close, sendCode, verifyCode,
        back: () => { stopTimer(); proofRef.current = null; setStage('value'); setCodeState(''); setError(''); },
    };
}
